import * as React from "react";
import { IconSearch, IconX } from "@tabler/icons-react";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "./select";
import { cn } from "../../lib/utils";

const FilterBar = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-wrap items-center gap-3 mb-4", className)}
    {...props}
  />
));
FilterBar.displayName = "FilterBar";

const FilterBarSearch = React.forwardRef(
  (
    {
      className,
      placeholder = "Search...",
      value,
      onChange,
      ...props
    },
    ref
  ) => (
    <div ref={ref} className={cn("relative w-64", className)}>
      <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary pointer-events-none" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className="pl-9 h-9"
        {...props}
      />
    </div>
  )
);
FilterBarSearch.displayName = "FilterBarSearch";

const FilterBarFilter = React.forwardRef(
  (
    {
      className,
      label,
      value,
      onChange,
      options = [],
      placeholder = "All",
      includeAll = true,
      allValue = "__all__",
      ...props
    },
    ref
  ) => (
    <Select value={value} onValueChange={onChange} {...props}>
      <SelectTrigger ref={ref} className={cn("h-9 w-auto min-w-[120px]", className)}>
        {label && <span className="text-text-tertiary mr-1.5 text-xs">{label}:</span>}
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {includeAll && <SelectItem value={allValue}>{placeholder}</SelectItem>}
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
);
FilterBarFilter.displayName = "FilterBarFilter";

const FilterBarChip = React.forwardRef(
  ({ className, children, onRemove, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1 bg-accent-muted text-accent text-xs px-3 py-1",
        className
      )}
      {...props}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 hover:text-accent-hover"
        >
          <IconX className="h-3 w-3" />
        </button>
      )}
    </span>
  )
);
FilterBarChip.displayName = "FilterBarChip";

const FilterBarClearAll = React.forwardRef(
  ({ className, children = "Clear all", ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "text-xs text-text-tertiary hover:text-text-secondary underline cursor-pointer",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
FilterBarClearAll.displayName = "FilterBarClearAll";

export {
  FilterBar,
  FilterBarSearch,
  FilterBarFilter,
  FilterBarChip,
  FilterBarClearAll,
};
